import { useEffect, useRef, useState } from 'react';

import useCopy from 'lib/hooks/useCopy';
import usePageTitle from 'lib/hooks/usePageTitle';

import { withErrorBoundary } from 'components/shared/Error/Boundary';
import { IconSvg } from 'components/shared/Icon/lib/Icon.generated';
import Input from 'components/shared/Input/Input';

import CategorySection from './components/CategorySection';
import { getIconCategory, getIconFamily, getIconType } from './lib/helpers';

const IconsPage = () => {
  const [iconData, setIconData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredSections, setFilteredSections] = useState({});
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);
  const { copyToClipboard } = useCopy();

  useEffect(() => {
    // Focus the search input when component mounts
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }

    const iconNames = Object.keys(IconSvg);

    // Add category information to each icon
    const categorizedIcons = iconNames.map((name) => ({
      name,
      width: 0,
      family: getIconFamily(name),
      category: getIconCategory(name),
      type: getIconType(name)
    }));

    // Sort by category first, then by family, then by icon type alphabetically
    categorizedIcons.sort((a, b) => {
      if (a.category !== b.category) return a.category - b.category;
      if (a.family !== b.family) return a.family.localeCompare(b.family);

      // For standard icon types, maintain preferred order
      const standardTypeOrder = { linear: 0, filled: 1, mono: 2 };
      const aTypeOrder = standardTypeOrder[a.type];
      const bTypeOrder = standardTypeOrder[b.type];

      // If both are standard types, use the preferred order
      if (aTypeOrder !== undefined && bTypeOrder !== undefined) {
        return aTypeOrder - bTypeOrder;
      }

      // Otherwise, sort alphabetically by type
      return a.type.localeCompare(b.type);
    });

    setIconData(categorizedIcons);
  }, []);

  // Group icons by category and then by family (for icons in folders with 2+ items)
  useEffect(() => {
    const sections = iconData.reduce((acc, icon) => {
      // Filter by search term if one exists
      if (searchTerm && !icon.name.toLowerCase().includes(searchTerm.toLowerCase())) {
        return acc;
      }

      const category = icon.category;
      if (!acc[category]) acc[category] = { grouped: {}, individual: [] };

      // Exclude certain prefixes from grouping
      const excludedPrefixes = ['illustration', 'pattern', 'shape-', 'background', 'logo-'];
      const shouldExcludeFromGrouping = excludedPrefixes.some((prefix) =>
        icon.name.toLowerCase().startsWith(prefix)
      );

      // Group icons that have a folder structure (family is not 'unknown') and are not excluded
      if (
        (icon.family !== 'unknown' && !shouldExcludeFromGrouping) ||
        ['linear', 'filled', 'mono'].some((type) => icon.name.includes(type))
      ) {
        if (!acc[category].grouped[icon.family]) {
          acc[category].grouped[icon.family] = [];
        }
        acc[category].grouped[icon.family].push(icon);
      } else {
        // Add to individual icons (icons without folder structure or excluded icons)
        acc[category].individual.push(icon);
      }

      return acc;
    }, {});

    // Move groups with less than 2 items to individual section, except for groups with standard icon types
    Object.keys(sections).forEach((category) => {
      const categoryData = sections[category];
      const groupsToRemove = [];

      Object.entries(categoryData.grouped).forEach(([family, icons]) => {
        if (icons.length < 2) {
          // Check if any icon in this group contains standard types
          const hasStandardTypes = icons.some((icon) =>
            ['linear', 'filled', 'mono'].some((type) => icon.name.includes(type))
          );

          if (!hasStandardTypes) {
            // Move single icons to individual section only if they don't have standard types
            categoryData.individual.push(...icons);
            groupsToRemove.push(family);
          }
        }
      });

      // Remove single-item groups that were moved to individual
      groupsToRemove.forEach((family) => {
        delete categoryData.grouped[family];
      });
    });

    setFilteredSections(sections);
  }, [iconData, searchTerm]);

  usePageTitle('Icons');

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  return (
    <div className="h-[100vh] bg-secondary-50 p-4">
      <div className="fixed right-1/2 top-0 z-50 translate-x-1/2 rounded-b-lg bg-secondary-50 p-4">
        <Input
          type="text"
          placeholder="Search icon"
          value={searchTerm}
          onChange={handleSearchChange}
          className="w-[300px]"
          inputWrapperClassName="focus-within:border-secondary-300 focus-within:outline-[#E0E3F9]"
          autoFocus
        />
      </div>
      <div ref={containerRef}>
        {Object.entries(filteredSections).map(([category, { grouped, individual }]) => (
          <CategorySection
            key={category}
            category={category}
            grouped={grouped}
            individual={individual}
            onIconClick={copyToClipboard}
          />
        ))}
      </div>
    </div>
  );
};

export default withErrorBoundary(IconsPage);
